import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Site access gate
export const gateStatus = () => api.get('/gate/status').then((r) => r.data);
export const verifyGate = (password) => api.post('/gate/verify', { password }).then((r) => r.data);

// Auth
export const loginByEmail = (email) =>
  api.post('/auth/login', { email: email.trim().toLowerCase() }).then((r) => r.data);
export const checkEmail = (email) =>
  api.get(`/auth/check-email/${encodeURIComponent(email)}`).then((r) => r.data);
export const createUser = (payload) => api.post('/users', payload).then((r) => r.data);
export const getUser = (id) => api.get(`/users/${id}`).then((r) => r.data);
export const updateUser = (id, updates) => api.put(`/users/${id}`, updates).then((r) => r.data);
export const completeOnboarding = (id) =>
  api.put(`/users/${id}/complete-onboarding`).then((r) => r.data);
export const uploadAvatar = (id, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/users/${id}/avatar`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
};
export const fileUrl = (path) => (path ? `${BACKEND_URL}${path}` : null);
export const getConsentDefinitions = () => api.get('/consent/definitions').then((r) => r.data);
export const getConsent = (patientId) => api.get(`/consent/${patientId}`).then((r) => r.data);
export const saveConsent = (patientId, consentType, consented) =>
  api.post('/consent', { patient_id: patientId, consent_type: consentType, consented }).then((r) => r.data);
export const deleteAccount = (userId) => api.delete(`/users/${userId}`).then((r) => r.data);
export const createStaffAccount = (payload) =>
  api.post('/auth/create-staff-account', payload).then((r) => r.data);
export const getStaffAccounts = () => api.get('/auth/staff-accounts').then((r) => r.data);

// Dashboard / scores / check-ins
export const getDashboard = (userId) => api.get(`/dashboard/${userId}`).then((r) => r.data);
export const getLatestScore = (userId) =>
  api.get(`/health-scores/${userId}/latest`).then((r) => r.data);
export const createCheckIn = (payload) => api.post('/check-ins', payload).then((r) => r.data);
export const getTodayCheckIn = (userId) =>
  api.get(`/check-ins/${userId}/today`).then((r) => r.data);
export const getCheckInHistory = (userId, days = 30) =>
  api.get(`/check-ins/${userId}/history?days=${days}`).then((r) => r.data);

// Domain check-ins
export const saveDomainCheckin = (payload) =>
  api.post('/domain-checkins', payload).then((r) => r.data);
export const getDomainCheckins = (userId, domain, days = 90) =>
  api
    .get(`/domain-checkins/${userId}?days=${days}${domain ? `&domain=${domain}` : ''}`)
    .then((r) => r.data);
export const getLatestDomainCheckin = (userId, domain) =>
  api.get(`/domain-checkins/${userId}/latest/${domain}`).then((r) => r.data);

// Weight
export const logWeight = (payload) => api.post('/weight-tracker', payload).then((r) => r.data);
export const getWeightHistory = (userId) =>
  api.get(`/weight-tracker/${userId}`).then((r) => r.data);

// Insights
export const generateInsight = (userId) =>
  api.post(`/insights/generate/${userId}`).then((r) => r.data);
export const getInsights = (userId) => api.get(`/insights/${userId}`).then((r) => r.data);

// Precision health
export const submitAssessment = (payload) =>
  api.post('/precision-health/assess', payload).then((r) => r.data);
export const getLatestAssessment = (userId) =>
  api.get(`/precision-health/${userId}/latest`).then((r) => r.data);
export const getPremiumInsights = (userId) =>
  api.get(`/precision-health/${userId}/premium-insights`).then((r) => r.data);

// Subscriptions
export const getSubscription = (userId) =>
  api.get(`/subscriptions/${userId}`).then((r) => r.data);
export const createSubscription = (userId, planType) =>
  api.post(`/subscriptions?user_id=${userId}&plan_type=${planType}`).then((r) => r.data);

// Appointments
export const createAppointment = (payload) =>
  api.post('/appointments', payload).then((r) => r.data);
export const getAppointments = (userId) =>
  api.get(`/appointments/${userId}`).then((r) => r.data);
export const cancelAppointment = (appointmentId) => api.put(`/appointments/${appointmentId}/cancel`).then((r) => r.data);
export const getOpenSlots = () => api.get('/appointments/open-slots').then((r) => r.data);
export const getClinicianAvailability = (clinicianId) => api.get(`/clinician/${clinicianId}/availability`).then((r) => r.data);
export const addClinicianAvailability = (clinicianId, payload) => api.post(`/clinician/${clinicianId}/availability`, payload).then((r) => r.data);
export const removeClinicianAvailability = (clinicianId, slotId) => api.delete(`/clinician/${clinicianId}/availability/${slotId}`).then((r) => r.data);
export const getClinicianAppointments = (clinicianId) => api.get(`/clinician/${clinicianId}/appointments`).then((r) => r.data);
export const decideAppointment = (clinicianId, appointmentId, decision, note) =>
  api.put(`/clinician/${clinicianId}/appointments/${appointmentId}/${decision}`, { note }).then((r) => r.data);
export const updateWellbeingStatement = (userId, text) => api.put(`/users/${userId}/wellbeing-statement`, { text }).then((r) => r.data);

// Biomarkers
export const getReferenceRanges = () =>
  api.get('/biomarker-reference-ranges').then((r) => r.data);
export const extractBiomarkers = (file, meta = {}) => {
  const fd = new FormData();
  fd.append('file', file);
  if (meta.patientId) fd.append('patient_id', meta.patientId);
  if (meta.uploadedBy) fd.append('uploaded_by', meta.uploadedBy);
  return api
    .post('/biomarkers/extract', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 90000 })
    .then((r) => r.data);
};
export const updateClinicianPatient = (clinicianId, patientId, payload) =>
  api.put(`/clinician/${clinicianId}/patient/${patientId}`, payload).then((r) => r.data);
export const getLabUploads = (clinicianId, patientId) =>
  api.get(`/clinician/${clinicianId}/lab-uploads`, { params: patientId ? { patient_id: patientId } : {} }).then((r) => r.data);
export const getPatientRecord = (clinicianId, patientId) =>
  api.get(`/clinician/${clinicianId}/patient/${patientId}/record`).then((r) => r.data);
export const updateResultRelease = (clinicianId, patientId, biomarkerId, payload) =>
  api.put(`/clinician/${clinicianId}/patient/${patientId}/results/${biomarkerId}/release`, payload).then((r) => r.data);
export const getReleasedBiomarkers = (patientId) => api.get(`/biomarkers/${patientId}/released`).then((r) => r.data);
export const saveBiomarkers = (payload) => api.post('/biomarkers', payload).then((r) => r.data);
export const getPatientBiomarkers = (patientId) => api.get(`/biomarkers/${patientId}`).then((r) => r.data);
export const analyzeBiomarkers = (biomarkerId) =>
  api.post(`/biomarkers/${biomarkerId}/analyze`).then((r) => r.data);
export const getLatestBiomarkerAnalysis = (patientId) =>
  api.get(`/biomarker-analysis/${patientId}/latest`).then((r) => r.data);

// Messaging & reminders
export const getMessages = (userId) => api.get(`/messages/${userId}`).then((r) => r.data);
export const sendMessage = (payload) => api.post('/messages', payload).then((r) => r.data);
export const markMessageRead = (messageId) =>
  api.put(`/messages/${messageId}/read`).then((r) => r.data);
export const getNotifications = (userId, unreadOnly = false) =>
  api.get(`/notifications/${userId}?unread_only=${unreadOnly}`).then((r) => r.data);
export const createReminder = (userId, title, message) =>
  api
    .post(
      `/notifications?user_id=${userId}&notification_type=consultation_reminder&title=${encodeURIComponent(
        title
      )}&message=${encodeURIComponent(message)}`
    )
    .then((r) => r.data);
export const markNotificationRead = (id) =>
  api.put(`/notifications/${id}/read`).then((r) => r.data);
export const getMessageUnreadCount = (userId) =>
  api.get(`/messages/${userId}/unread-count`).then((r) => r.data);
export const getNotificationUnreadCount = (userId) =>
  api.get(`/notifications/${userId}/unread-count`).then((r) => r.data);
export const markAllMessagesRead = (userId) =>
  api.put(`/messages/${userId}/mark-all-read`).then((r) => r.data);

// Analytics
export const getWeeklyDigest = (userId) =>
  api.get(`/analytics/weekly-digest/${userId}`).then((r) => r.data);
export const getSymptomClusters = (userId) =>
  api.get(`/analytics/symptom-clusters/${userId}`).then((r) => r.data);

// Testimonials
export const getTestimonials = () => api.get('/testimonials').then((r) => r.data);

// Clinician
export const createClinicianPatient = (clinicianId, payload) =>
  api.post(`/clinician/${clinicianId}/patients`, payload).then((r) => r.data);
export const getClinicianDashboard = (clinicianId) =>
  api.get(`/clinician/${clinicianId}/dashboard`).then((r) => r.data);
export const getPatientSummary = (clinicianId, patientId) =>
  api.get(`/clinician/${clinicianId}/patient/${patientId}/summary`).then((r) => r.data);
export const saveClinicalRecord = (clinicianId, patientId, payload) =>
  api
    .post(`/clinician/${clinicianId}/patient/${patientId}/clinical-record`, payload)
    .then((r) => r.data);
export const updateTriageStatus = (clinicianId, patientId, status) =>
  api
    .put(`/clinician/${clinicianId}/patient/${patientId}/triage-status?status=${status}`)
    .then((r) => r.data);
export const getClinicalOptions = () =>
  Promise.all([
    api.get('/clinical-options/primary-drivers'),
    api.get('/clinical-options/secondary-contributors'),
    api.get('/clinical-options/treatment-components'),
    api.get('/clinical-options/follow-up-intervals'),
  ]).then(([a, b, c, d]) => ({
    primaryDrivers: a.data,
    secondaryContributors: b.data,
    treatmentComponents: c.data,
    followUpIntervals: d.data,
  }));

// Admin
export const getAdminUsers = () => api.get('/admin/users').then((r) => r.data);
export const getAdminStats = () => api.get('/admin/stats').then((r) => r.data);
export const getAdminAuditLogs = () => api.get('/admin/audit-logs').then((r) => r.data);
export const getClinicians = () => api.get('/clinicians').then((r) => r.data);
export const assignClinician = (patientId, clinicianId) =>
  api.post('/admin/assign-clinician', { patient_id: patientId, clinician_id: clinicianId }).then((r) => r.data);
